import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { Fragment, useContext, useState } from 'react';

import { SkillsBarChartColumns } from './SkillsBarChartColumns';
import { TableBar } from './TableBar';
import { TableBarSkillSearch } from './TableBarSkillSearch';
import { usePersistTableSort } from './use-persist-table-sort';
import { defaultPagination } from './use-talent-directory-params';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import { ContentWrapper } from '../../../design/components/containers/ContentWrapper';
import { TableLoadingSpinner } from '../../../design/components/LoadingSpinner';
import { pageSizeOptions } from '../../common/data-grids/common/constants';
import { GeneralDataGrid } from '../../common/data-grids/common/GeneralDataGrid';
import { useTablePagination } from '../../common/data-grids/common/use-table-pagination';
import { ComponentErrorBoundary } from '../../error-handling/ErrorBoundaries';
import { useSnackbars } from '../../error-handling/SnackBars';
import { useGlobalPermissions } from '../../query-hooks/global-permissions';
import { useTalentDirectorySkillsExport } from '../../query-hooks/talent-directory/talent-directory';
import { useGlobalSearchDirectorySkills } from '../../query-hooks/talent-directory/use-global-search-directory-skills';
import { useTalentDirectorySkillFilter } from '../../query-hooks/use-talent-directory-skill-filter';
import { Shade } from '../../theming';

import type { TableBarSortOption } from './TableBar';
import type { GeneralFilter } from '../../../../../shared/source/data-contract/filter';
import type { SkillShort } from '../../../../../shared/source/data-contract/skill';
import type { GlobalSearchDirectorySkillsSortOption } from '../../query-hooks/talent-directory/use-global-search-directory-skills';
import type { GridRowParams } from '@mui/x-data-grid-pro';
import type { FunctionComponent } from 'react';
import type Maybe from 'true-myth/maybe';

const SkillsTableWithSkillGap: FunctionComponent<{
    readonly filterPayload: Maybe<GeneralFilter>;
    readonly sortOptions: readonly GlobalSearchDirectorySkillsSortOption[];
}> = ({ filterPayload, sortOptions }) => {
    const [openRowId, setOpenRowId] = useState<string>();
    const { translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();
    const { data, isFetching, isPending } = useGlobalSearchDirectorySkills({
        filterPayload: filterPayload.unwrapOr(undefined),
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortOptions,
    });

    const { data: globalPermissions } = useGlobalPermissions();
    const withLearningOpportunities = globalPermissions?.canSearchLearningOpportunities ?? false;

    const skills = data?.data ?? [];
    const pagination = data?.pagination ?? defaultPagination;

    const handleRowClick = (params: GridRowParams): void => {
        const rowId = params.id.toString();
        if (rowId === openRowId) {
            setOpenRowId(undefined);

            return;
        }
        setOpenRowId(rowId);
    };

    return (
        <GeneralDataGrid
            columns={SkillsBarChartColumns({ openRowId, withLearningOpportunities })}
            disableMultipleColumnsSorting={true}
            disableRowSelectionOnClick={true}
            getRowClassName={(params) => (openRowId === params.id.toString() ? 'Mui-selected-row' : '')}
            getRowId={({ id }) => id}
            loading={isPending || isFetching}
            localeText={{ noRowsLabel: translate('pages.global-search.no-data.skills') }}
            pageSizeOptions={pageSizeOptions}
            pagination={true}
            paginationMode="server"
            paginationModel={paginationModel}
            rowCount={pagination.count}
            rows={skills}
            slots={{
                loadingOverlay: TableLoadingSpinner,
            }}
            sx={{
                '& .Mui-selected-row': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, Shade.p8),
                },
                '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                },
                '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                    width: '100%',
                },
                border: 'none',
                height: '70vh',
            }}
            onPaginationModelChange={setPaginationModel}
            onRowClick={handleRowClick}
        />
    );
};

const defaultSortField = 'relevance';

type SkillTableBarSortOption = TableBarSortOption<typeof defaultSortField | 'skillGapRelative' | 'title'>;

export const SkillsTable: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const [selectedSkills, setSelectedSkills] = useState<readonly SkillShort[]>([]);
    const [sortOption, saveTableBarSortOption] = usePersistTableSort('talentDirectorySkillSort');
    const currentTableBarSortOption = sortOption.at(0) ?? {
        direction: 'desc',
        field: defaultSortField,
    };
    const [isSkillSearchFieldOpen, setIsSkillSearchFieldOpen] = useState<boolean>(false);
    const { data, isSuccess } = useTalentDirectorySkillFilter(selectedSkills.map(({ id }) => id));
    const sortOptions: readonly SkillTableBarSortOption[] = [
        {
            direction: 'desc',
            field: 'relevance',
            label: translate('pages.talent-directory.sort-options.skills.byRelevance'),
        },
        { direction: 'asc', field: 'title', label: translate('pages.talent-directory.sort-options.skills.AtoZ') },
        { direction: 'desc', field: 'title', label: translate('pages.talent-directory.sort-options.skills.ZtoA') },
        {
            direction: 'asc',
            field: 'skillGapRelative',
            label: translate('pages.talent-directory.sort-options.skills.lowestToHighest'),
        },
        {
            direction: 'desc',
            field: 'skillGapRelative',
            label: translate('pages.talent-directory.sort-options.skills.highestToLowest'),
        },
    ];
    const hasActiveSortOption = currentTableBarSortOption.field !== defaultSortField;
    const { isPending: isExporting, mutate: exportSkills } = useTalentDirectorySkillsExport();
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();

    const onExportButtonClick = (): void => {
        exportSkills(undefined, {
            onError: showErrorSnackbar,
            onSuccess: ({ downloadLink }) => {
                showSuccessSnackbar('success.skills.exported');
                window.open(downloadLink, '_blank');
            },
        });
    };

    const onSkillSearchCancelButtonClick = (): void => {
        setIsSkillSearchFieldOpen(false);
        setSelectedSkills([]);
    };

    return isSuccess ? (
        <Fragment>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'flex-end',
                    minHeight: '56px',
                    px: 2,
                }}
            >
                {isSkillSearchFieldOpen ? (
                    <TableBarSkillSearch
                        selectedSkills={selectedSkills}
                        onSearchBarCancelButtonClick={onSkillSearchCancelButtonClick}
                        onSkillDeselected={(skill) =>
                            setSelectedSkills(selectedSkills.filter(({ id }) => id !== skill.id))
                        }
                        onSkillSearchButtonClick={setSelectedSkills}
                        onSkillSelected={(skill) => setSelectedSkills([...selectedSkills, skill])}
                    />
                ) : (
                    <TableBar
                        filters={data.filters}
                        hasActiveFilters={data.hasActiveFilters}
                        hasActiveSortOption={hasActiveSortOption}
                        isExporting={isExporting}
                        resetFilterValues={data.resetFilterValues}
                        saveFilterValues={data.saveFilterValues}
                        sortOptions={sortOptions}
                        onExportButtonClick={onExportButtonClick}
                        onSearchButtonClick={() => setIsSkillSearchFieldOpen(true)}
                        onSortOptionSelected={({ direction, field }) =>
                            saveTableBarSortOption([
                                {
                                    direction,
                                    field,
                                },
                            ])
                        }
                    />
                )}
            </Box>
            <ContentWrapper>
                <ComponentErrorBoundary message="errors.component.global-search.skills">
                    <SkillsTableWithSkillGap
                        filterPayload={data.filterPayload}
                        sortOptions={[currentTableBarSortOption]}
                    />
                </ComponentErrorBoundary>
            </ContentWrapper>
        </Fragment>
    ) : null;
};
